// Excel експортът на „Дневни отчети → Заведение".
//
// Редовете идват ФИЛТРИРАНИ и ПОДРЕДЕНИ от таблицата (`onFilteredRowsChange`),
// не суровият отговор: файлът повтаря екрана, включително филтъра по съставил
// в колоната. Сумите излизат като ЧИСЛА, за да се сумират в Excel.
//
// Разликата спрямо екрана е една: тук всеки метод на плащане получава
// собствени колони (сума + брой сметки). На екрана мястото стига само за
// в брой / карта / „Друго", но сверката с касата се прави в таблица — и точно
// това показваше старата цветна лента „Разпределение по методи", която този
// екран вече няма.
//
// БЕЗ собствен времеви печат — `exportToExcel` вече лепи `_${stamp}`.

import dayjs from "dayjs";
import type { TFunction } from "i18next";

import { exportToExcel, type ExcelColumn } from "../../../../utils/excelExport";
import { parseDecimal } from "../../../../components/dataTable";
import { buildEntriesFromBreakdowns } from "../../../../utils/paymentMethodsDisplay";
import type { EstablishmentReport } from "../../../../api/reports";
import { preparedByLabel } from "./establishmentReportsMath";

type Options = {
  t: TFunction;
  /** Има ли платено извън в брой и карта — колоната следва таблицата. */
  showOther: boolean;
  /** Езикът решава кое име на метод се изписва (BE връща и двете). */
  isBg: boolean;
};

type MethodTotals = {
  amounts: Map<number, number>;
  counts: Map<number, number>;
};

/**
 * Сумата и броят сметки по метод за всеки отчет — сметнати ВЕДНЪЖ, преди
 * колоните. Иначе всяка клетка би обхождала наново `payment_breakdown` на реда
 * си: при десет метода това са десет обхождания на ред.
 */
const methodTotalsByReport = (
  rows: readonly EstablishmentReport[],
): Map<number, MethodTotals> => {
  const byReport = new Map<number, MethodTotals>();
  for (const row of rows) {
    const totals: MethodTotals = { amounts: new Map(), counts: new Map() };
    for (const item of row.payment_breakdown ?? []) {
      totals.amounts.set(item.payment_method_id, parseDecimal(item.amount));
      totals.counts.set(item.payment_method_id, parseDecimal(item.bills_count));
    }
    byReport.set(row.id, totals);
  }
  return byReport;
};

const round = (value: number) => Number(value.toFixed(2));

export const exportEstablishmentReports = (
  rows: readonly EstablishmentReport[],
  { t, showOther, isBg }: Options,
) => {
  const title = t("reportsPage.dailyReportsTitle");

  const columns: ExcelColumn<EstablishmentReport>[] = [
    {
      header: t("reportsPage.reportNumber"),
      value: (row) => row.report_number,
      width: 10,
    },
    {
      header: t("reportsPage.dailyWorkDay"),
      value: (row) => dayjs(row.report_date).format("DD.MM.YYYY"),
      width: 14,
    },
    {
      header: t("reportsPage.establishmentPreparedBy"),
      value: (row) => preparedByLabel(row),
      width: 24,
    },
    {
      header: t("reportsPage.personalReportsCount"),
      value: (row) => Number(row.total_personal_reports ?? 0),
      width: 16,
    },
    {
      header: t("reportsPage.bills"),
      value: (row) => Number(row.total_bills_count ?? 0),
      width: 10,
    },
    {
      header: t("reportsPage.cashAmount"),
      value: (row) => parseDecimal(row.cash_amount),
      width: 14,
    },
    {
      header: t("reportsPage.cardAmount"),
      value: (row) => parseDecimal(row.card_amount),
      width: 14,
    },
  ];

  if (showOther) {
    columns.push({
      header: t("reportsPage.dailyOther"),
      value: (row) => parseDecimal(row.other_amount),
      width: 14,
    });
  }

  columns.push({
    header: t("reportsPage.dailyColTotal"),
    value: (row) => parseDecimal(row.total_amount),
    width: 14,
  });

  // Колоните по МЕТОД отговарят на друг въпрос от кеш/карта: там смесеното
  // плащане е разпределено пропорционално, тук всеки метод носи цялата си сума
  // така, както е платено. Заглавието го казва — иначе „В брой" се среща два
  // пъти с различни числа.
  const methodEntries = buildEntriesFromBreakdowns(
    rows.map((row) => row.payment_breakdown),
    // Процентите не влизат в експорта — знаменателят тук е без значение.
    0,
    isBg,
  );
  const totalsByReport = methodTotalsByReport(rows);
  for (const entry of methodEntries) {
    columns.push(
      {
        header: t("reportsPage.methodAmountColumn", { method: entry.label }),
        value: (row) =>
          round(totalsByReport.get(row.id)?.amounts.get(entry.id) ?? 0),
        width: 16,
      },
      {
        header: t("reportsPage.methodCountColumn", { method: entry.label }),
        value: (row) => totalsByReport.get(row.id)?.counts.get(entry.id) ?? 0,
        width: 12,
      },
    );
  }

  columns.push({
    header: t("reportsPage.createdAt"),
    value: (row) => dayjs(row.created_at).format("DD.MM.YYYY HH:mm:ss"),
    width: 20,
  });

  exportToExcel<EstablishmentReport>({
    filename: title,
    sheetName: t("reportsPage.establishmentReports"),
    columns,
    rows: [...rows],
  });
};
